import { EmissionsCategoryEnum } from '@shared/api/types';
import { cn } from '@shared/lib/utils';
import Unit from './content/unit';
import RatingCloud from './ratings/rating-cloud';
import { SimpleTooltip } from './simple-tooltip';

interface Props {
  value: number;
  rating: EmissionsCategoryEnum;
  methodologyVersion: number;
  showValue?: boolean;
  showClouds?: boolean;
  showBandTooltips?: boolean;
  className?: string;
}

// Upper bounds for each rating (E is unbounded — handled via methodologyVersion upper limit)
const THRESHOLDS = [1.75, 3.0, 4.25, 5.5] as const;

// Returns the numeric range string for a given band index (0=A … 4=E)
export function getBandRange(index: number): string {
  const lower = index === 0 ? 0 : THRESHOLDS[index - 1];
  if (index === 4) return `> ${THRESHOLDS[3]}`;
  return `${lower} \u2013 ${THRESHOLDS[index]}`;
}

export const ratingSections = [
  { rating: 'A' as EmissionsCategoryEnum, bgColor: 'var(--rating-a)' },
  { rating: 'B' as EmissionsCategoryEnum, bgColor: 'var(--rating-b)' },
  { rating: 'C' as EmissionsCategoryEnum, bgColor: 'var(--rating-c)' },
  { rating: 'D' as EmissionsCategoryEnum, bgColor: 'var(--rating-d)' },
  { rating: 'E' as EmissionsCategoryEnum, bgColor: 'var(--rating-e)' },
];

export default function NewRatingsThresholdSlider({
  value,
  methodologyVersion,
  showValue = true,
  showClouds = true,
  showBandTooltips = false,
  className,
  rating,
}: Props) {
  const eUpperLimit = methodologyVersion < 2 ? 6.125 : 6.75;

  // Piecewise linear mapping so each section occupies exactly 20% of the bar
  const equivalentPerc = (v: number): number => {
    const sw = 20;
    if (v <= 0) return 1;
    if (v <= THRESHOLDS[0]) return (v / THRESHOLDS[0]) * sw;
    if (v <= THRESHOLDS[1])
      return sw + ((v - THRESHOLDS[0]) / (THRESHOLDS[1] - THRESHOLDS[0])) * sw;
    if (v <= THRESHOLDS[2])
      return (
        2 * sw + ((v - THRESHOLDS[1]) / (THRESHOLDS[2] - THRESHOLDS[1])) * sw
      );
    if (v <= THRESHOLDS[3])
      return (
        3 * sw + ((v - THRESHOLDS[2]) / (THRESHOLDS[3] - THRESHOLDS[2])) * sw
      );
    return Math.min(
      4 * sw + ((v - THRESHOLDS[3]) / (eUpperLimit - THRESHOLDS[3])) * sw,
      98
    );
  };

  const perc = equivalentPerc(value);
  const indicatorColor = ratingSections.find(
    (s) => s.rating === rating
  )?.bgColor;

  return (
    <section className={cn('flex flex-col', className)}>
      {showClouds && (
        <div className="mb-2 flex items-end">
          {ratingSections.map((section) => (
            <div key={section.rating} className="flex flex-1 justify-center">
              <RatingCloud
                rating={section.rating}
                className="grow-0 object-contain"
                format="png"
              />
            </div>
          ))}
        </div>
      )}

      {/* Bars and circle indicator in one container — no extra height below when showValue=false */}
      <div className="relative flex h-[10px] flex-row overflow-visible">
        {ratingSections.map((section, index) => {
          const band = (
            <div
              key={section.rating}
              className={cn(
                'flex-1 rounded-[3px] ml-0.5',
                index < ratingSections.length - 1 && 'mr-px'
              )}
              style={{ backgroundColor: section.bgColor }}
            />
          );
          if (!showBandTooltips) return band;
          return (
            <SimpleTooltip
              key={section.rating}
              trigger={band}
              content={
                <div className="flex items-center gap-2">
                  <RatingCloud
                    rating={section.rating}
                    className="!w-8 shrink-0"
                  />
                  <span className="flex items-baseline gap-1">
                    {getBandRange(index)}
                    <Unit variant="kgCO2e/kg" />
                  </span>
                </div>
              }
              side="top"
            />
          );
        })}
        <div
          className="absolute top-1/2 size-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 bg-gray-00 outline outline-2 outline-gray-00"
          style={{ left: `${perc}%`, borderColor: indicatorColor }}
        />
      </div>

      {showValue && (
        <div className="relative mt-3 h-8 w-full">
          <div
            className="absolute h-[10px] w-[2px] -translate-x-1/2 border-2 border-[#efeff7] bg-gray-00"
            style={{ left: `${perc}%` }}
          />
          <div
            className="absolute mt-[10px] flex text-xs leading-3"
            style={{
              left: perc > 60 ? `calc(${perc}% - 114px)` : `${perc}%`,
            }}
          >
            {value} <Unit variant="kgCO2e/kg" className="ml-1 inline" />
          </div>
        </div>
      )}
    </section>
  );
}
